/* eslint-disable */
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dialog, Disclosure, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  BookmarkIcon,
  ListBulletIcon,
  ClockIcon,
  GiftIcon,
  ArrowRightOnRectangleIcon,
  ArrowsRightLeftIcon,
  PencilSquareIcon,
  UserCircleIcon,
  WalletIcon,
  StarIcon,
  PlayIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCaretDown,
  faCrown,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import SearchBar from './../../components/SearchBar';
import Logout from './../../components/buttons/Logout';
import Context from '../../context/context';

import logoLt from '../../assets/images/pcdl-logo-lt.png';
import logoSm from '../../assets/images/logo/pcdl_logo_single.png';
import PromoBanner from './../components/PromoBanner.tsx';
import { useExposeCampaign } from '../data/exposeCampaign.ts';

/* ------------------------------ Utils ---------------------------------- */

function cx(...cls) {
  return cls.filter(Boolean).join(' ');
}

function readJSON(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function getSession() {
  return readJSON('pcdl.store', null);
}

/** Small event helper so other code can notify this nav without a full state manager */
export function broadcastSessionUpdated() {
  try {
    window.dispatchEvent(new CustomEvent('pcdl:session-updated'));
  } catch {
    /* noop */
  }
}

/** ---- Simple local cache helpers for geo ---- */
const GEO_KEY = 'pcdl.geo';
function readGeoCache() {
  try {
    const raw = localStorage.getItem(GEO_KEY);
    console.log('GEO raw:', raw, 'typeof:', typeof raw);
    if (!raw || raw === 'undefined' || raw === 'null') return null;

    const parsed = JSON.parse(raw);
    // accept either { country_code } or { country_name, country_code }
    if (
      parsed &&
      typeof parsed.country_code === 'string' &&
      parsed.country_code
    ) {
      return parsed;
    }
    return null;
  } catch (e) {
    // bad JSON in storage — clean it up so we don't keep failing
    try {
      localStorage.removeItem(GEO_KEY);
    } catch {}
    return null;
  }
}

function writeGeoCache(obj) {
  try {
    const safe = {
      country_code: (obj?.country_code || '').toLowerCase(),
      country_name: obj?.country_name || '',
      ts: Date.now(),
    };
    localStorage.setItem(GEO_KEY, JSON.stringify(safe));
  } catch {
    /* ignore */
  }
}

/* ---------------------------- Components ------------------------------- */

export default function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { translation } = useContext(Context);

  // Hide entirely on /signin (and nested)
  if (/^\/signin(\/|$)/i.test(location.pathname)) return null;

  // Hide the main nav items on /welcome
  const onWelcome = /^\/welcome(\/|$)/i.test(location.pathname);

  /* ----- Session state that stays in sync ----- */
  const [session, setSession] = useState(() => getSession());
  const loggedIn = Boolean(session && session.token);
  const wallet = useMemo(() => {
    const n =
      session && typeof session.wallet_balance === 'number'
        ? session.wallet_balance
        : Number(session?.wallet_balance) || 0;
    return n.toFixed(2);
  }, [session]);

  useEffect(() => {
    const applyFromStorage = () => {
      const next = getSession();
      setSession((curr) => {
        if (!curr && !next) return curr;
        if (!curr || !next) return next || null;
        if (
          curr.subscription_name !== next.subscription_name ||
          curr.wallet_balance !== next.wallet_balance ||
          curr.avatar_url !== next.avatar_url ||
          curr.token !== next.token
        ) {
          return next;
        }
        return curr;
      });
    };

    const onCustom = () => applyFromStorage();
    window.addEventListener('pcdl:session-updated', onCustom);

    const onStorage = (e) => {
      if (e.key === 'pcdl.store') applyFromStorage();
    };
    window.addEventListener('storage', onStorage);

    const onFocus = () => applyFromStorage();
    window.addEventListener('focus', onFocus);

    applyFromStorage();

    return () => {
      window.removeEventListener('pcdl:session-updated', onCustom);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  /* ----------------------------- Geo flag ----------------------------- */
  // Fallback flag if backend geo fails (set to '' to show nothing, or a two-letter code like 'ng')
  const GEO_FALLBACK_CODE = (
    process.env.REACT_APP_GEO_FALLBACK_CODE || ''
  ).toLowerCase(); // e.g., 'ng'

  const [countryCode, setCountryCode] = useState('');
  const [locReady, setLocReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function initGeo() {
      // 1) Try cache first
      const cached = readGeoCache();
      if (cached) {
        if (!cancelled) {
          setCountryCode(String(cached.country_code || '').toLowerCase());
          setLocReady(true);
        }
        return;
      }

      // 2) Call backend only (NEVER call ipstack from the browser)
      try {
        const url = `https://nmt.loveworldapis.com/api/pcdl/geo`;

        const headers = {};
        // If your backend enforces x-api-key on /affiliate/*, you can expose a *public* key in REACT_APP_API_KEY.
        // If you don't want any key in the browser, whitelist /affiliate/geo server-side.
        headers['x-api-key'] = 'ee115610-738b-4e7b-97e2-446468ba550c';

        const resp = await fetch(url, { headers });
        const json = await resp.json().catch(() => ({}));

        if (resp.ok && json?.status && json?.data) {
          const out = {
            country_code: (json.data.country_code || '').toLowerCase(),
            country_name: json.data.country_name || '',
          };
          writeGeoCache(out);
          if (!cancelled) {
            setCountryCode(out.country_code);
            setLocReady(true);
          }
          return;
        }

        // 3) Backend failed — use general fallback flag (still no ipstack in frontend)
        if (!cancelled) {
          if (GEO_FALLBACK_CODE) {
            setCountryCode(GEO_FALLBACK_CODE);
          }
          setLocReady(true);
        }
      } catch {
        if (!cancelled) {
          if (GEO_FALLBACK_CODE) {
            setCountryCode(GEO_FALLBACK_CODE);
          }
          setLocReady(true);
        }
      }
    }

    initGeo();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ----------------------------- Search UI ---------------------------- */
  const [searchOpen, setSearchOpen] = useState(false);

  /* --------------------------- Mobile sheets -------------------------- */
  const [mainOpen, setMainOpen] = useState(false); // main navigation (hamburger)
  const [profileOpen, setProfileOpen] = useState(false); // profile sheet

  /* ---------------------------- Promo banner -------------------------- */
  const PROMO_KEY = 'pcdl.promo.dismissed';
  const PROMO_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  const [promoOpen, setPromoOpen] = useState(false);
  // Promotes the live Exposé campaign (name from the Exposé API). Hidden while
  // it loads or when there's no campaign; dismissal is keyed by campaign so
  // dismissing one month doesn't hide the next.
  const exposeCampaign = useExposeCampaign();

  useEffect(() => {
    // Show promo unless dismissed within TTL
    try {
      const raw = localStorage.getItem(PROMO_KEY);
      if (raw) {
        const { ts } = JSON.parse(raw);
        if (Date.now() - Number(ts || 0) < PROMO_TTL) {
          setPromoOpen(false);
          return;
        }
      }
    } catch {
      /* ignore */
    }
    setPromoOpen(true);
  }, []);

  function dismissPromo() {
    try {
      localStorage.setItem(PROMO_KEY, JSON.stringify({ ts: Date.now() }));
    } catch {
      /* ignore */
    }
    setPromoOpen(false);
  }

  function MobileRow({ to, children, external, onAfter }) {
    const Cmp = external ? 'a' : Link;
    const props = external
      ? { href: to, target: '_blank', rel: 'noreferrer' }
      : { to };
    return (
      <Cmp
        {...props}
        onClick={() => {
          onAfter?.();
          setMainOpen(false);
        }}
        className="block w-full px-4 py-3 text-[15px] text-white hover:bg:white/10 hover:bg-white/10"
      >
        {children}
      </Cmp>
    );
  }

  function MobileCollapsible({ label, children }) {
    return (
      <Disclosure>
        {({ open }) => (
          <div className="border-t border-white/10">
            <Disclosure.Button className="w-full flex items-center justify-between px-4 py-3 text-[15px] text-white hover:bg-white/10">
              <span>{label}</span>
              <ChevronDownIcon
                className={cx(
                  'h-4 w-4 transition-transform',
                  open && 'rotate-180'
                )}
              />
            </Disclosure.Button>
            <Disclosure.Panel className="pb-2">
              <div className="pl-2">{children}</div>
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  }

  /* ---------- Small building blocks ---------- */

  function NavPill({ to, children }) {
    return (
      <Link to={to} className="nav-pill">
        {children}
      </Link>
    );
  }

  function FancyMenu({ children }) {
    return (
      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items className="menu-panel w-64">{children}</Menu.Items>
      </Transition>
    );
  }

  /* ------------ UPDATED: Menu-aware anchors that auto-close ------------- */
  function MenuAnchor({ to, children, external }) {
    const Cmp = external ? 'a' : Link;
    const props = external
      ? { href: to, target: '_blank', rel: 'noreferrer' }
      : { to };
    // Using Menu.Item ensures selection closes the dropdown
    return (
      <Menu.Item as={Cmp} {...props} className="menu-item">
        {children}
      </Menu.Item>
    );
  }

  function MenuLink({ to, children, className }) {
    return (
      <Menu.Item as={Link} to={to} className={cx('menu-item', className)}>
        {children}
      </Menu.Item>
    );
  }

  // Profile menu row — can render inside a Menu OR standalone (mobile sheet)
  function ProfileRow({
    to,
    onClick,
    icon: Icon,
    children,
    trailing,
    inMenu = false,
  }) {
    const baseClass =
      'flex w-full items-center gap-3 px-4 py-3 text-[15px] text-white/90 hover:bg-white/10 transition';

    const content = (
      <>
        <Icon className="h-5 w-5 text-white/70" />
        <span className="flex-1 text-left">{children}</span>
        {trailing || null}
      </>
    );

    if (inMenu) {
      // Inside Headless UI <Menu>: it auto-closes on selection
      if (to) {
        return (
          <Menu.Item as={Link} to={to} className={baseClass}>
            {content}
          </Menu.Item>
        );
      }
      return (
        <Menu.Item
          as="button"
          type="button"
          onClick={onClick}
          className={baseClass}
        >
          {content}
        </Menu.Item>
      );
    }

    // Standalone (mobile Dialog sheet): make sure clicks close the sheet too
    if (to) {
      return (
        <Link to={to} onClick={onClick} className={baseClass}>
          {content}
        </Link>
      );
    }
    return (
      <button type="button" onClick={onClick} className={baseClass}>
        {content}
      </button>
    );
  }

  /* -------------------------------- UI -------------------------------- */
  return (
    <header className="sticky top-0 z-40 bg-neutral-900/90 supports-[backdrop-filter]:bg-neutral-900/70 backdrop-blur border-b border-white/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="h-16 flex items-center justify-between">
          {/* Left cluster */}
          <div className="flex items-center gap-3">
            {/* Mobile hamburger -> opens MAIN SHEET (same style as profile) */}
            {!onWelcome && (
              <button
                type="button"
                onClick={() => setMainOpen(true)}
                className="lg:hidden inline-flex items-center justify-center rounded-md p-2 text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            )}

            {/* Brand */}
            <Link to="/" className="flex items-center">
              <img
                src={logoSm}
                alt="PCDL"
                className="h-8 ml-2 w-auto block lg:hidden"
              />
              <img
                src={logoLt}
                alt="PCDL"
                className="h-10 w-auto hidden lg:block"
              />
            </Link>

            {/* Desktop navigation — HIDDEN on /welcome */}
            {!onWelcome && (
              <nav className="hidden lg:flex items-center gap-1">
                <NavPill to="/">{t('Home')}</NavPill>

                {/* Browse */}
                <Menu as="div" className="relative">
                  <Menu.Button className="nav-pill">
                    {t('Browse')}
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      className="ml-1 h-3 w-3 opacity-80"
                    />
                  </Menu.Button>
                  <FancyMenu>
                    <MenuAnchor to="/categories">{t('Categories')}</MenuAnchor>
                    <MenuAnchor to="/browse">{t('Alphabetical')}</MenuAnchor>
                    <MenuAnchor to="/series/hGPFmF-VlVI/your-loveworld-specials">
                      {t('Your Loveworld Specials')}
                    </MenuAnchor>
                  </FancyMenu>
                </Menu>

                <NavPill to="/everydaysolutions">
                  {t('Everyday Solutions')}
                </NavPill>
                <NavPill to="/search">{t('PCDL Search')}</NavPill>

                {/* More */}
                <Menu as="div" className="relative">
                  <Menu.Button className="nav-pill">
                    {t('PCDL Affiliate')}
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      className="ml-1 h-3 w-3 opacity-80"
                    />
                  </Menu.Button>
                  <FancyMenu>
                    <MenuAnchor to="/affiliate/welcome">
                      {t('Welcome')}
                    </MenuAnchor>
                    <MenuAnchor to="/affiliate">{t('Dashboard')}</MenuAnchor>
                    <MenuAnchor to="/affiliate/everydaysolutions">
                      {t('Everyday Solutions')}
                    </MenuAnchor>
                  </FancyMenu>
                </Menu>

                {/* More */}
                <Menu as="div" className="relative">
                  <Menu.Button className="nav-pill">
                    {t('More')}
                    <FontAwesomeIcon
                      icon={faCaretDown}
                      className="ml-1 h-3 w-3 opacity-80"
                    />
                  </Menu.Button>
                  <FancyMenu>
                    <MenuAnchor to="/categories/XR7p8b5icds/languages">
                      {t('Languages')}
                    </MenuAnchor>
                    <MenuAnchor
                      to="https://pastorchrisdigitallibrary.org/livetv/"
                      external
                    >
                      {t('PCDL TV')}
                    </MenuAnchor>
                    <MenuAnchor to="/everydaysolutions/dictionary">
                      {t('Dictionary')}
                    </MenuAnchor>
                    <MenuAnchor to="/wordfest">{t('WordFest 7')}</MenuAnchor>
                    <MenuAnchor to="/4mot">
                      {t('4 Months Transformation Challenge')}
                    </MenuAnchor>
                  </FancyMenu>
                </Menu>
              </nav>
            )}
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Search icon */}
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="inline-flex items-center justify-center rounded-full p-2 text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
              title="Search"
              aria-label="Search"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>

            {/* Kids pill */}
            <Link to="/pcdl4kids" className="nav-pill">
              Kids
            </Link>

            {/* Profile / Auth */}
            {loggedIn ? (
              <>
                {/* Desktop dropdown (absolute; no layout shift) */}
                <Menu as="div" className="relative hidden lg:block">
                  <Menu.Button
                    aria-label="Open profile menu"
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full
                      bg-white/10 ring-1 ring-white/15
                      hover:bg-white/15 hover:ring-white/30
                      focus:outline-none focus:ring-2 focus:ring-white/30 overflow-hidden"
                  >
                    {session?.avatar_url ? (
                      <img
                        src={`https://nmt.loveworldapis.com/api/${session.avatar_url}`}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faUser}
                        className="h-4 w-4 text-white"
                      />
                    )}
                  </Menu.Button>

                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-150"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Menu.Items
                      className="absolute right-0 top-[calc(100%+8px)]
                                 w-[360px] max-w-[92vw] overflow-hidden
                                 rounded-xl border border-white/10 bg-neutral-900/95
                                 backdrop-blur shadow-2xl focus:outline-none z-50"
                    >
                      {/* Header */}
                      <div className="px-4 pt-4 pb-3 border-b border-white/10">
                        <div className="flex items-center">
                          <div className="h-12 w-12 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/15">
                            {session?.avatar_url ? (
                              <img
                                src={`https://nmt.loveworldapis.com/api/${session.avatar_url}`}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <UserCircleIcon className="h-full w-full text-white/40 p-1" />
                            )}
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="flex items-center">
                              <p className="truncate text-white font-semibold">
                                {session?.first_name ||
                                  session?.name ||
                                  'Profile'}
                              </p>
                              <button
                                type="button"
                                className="ml-2 rounded p-1 text-white/70 hover:bg-white/10"
                                onClick={() => navigate('/settings')}
                                title="Edit profile"
                              >
                                <PencilSquareIcon className="h-5 w-5" />
                              </button>
                            </div>
                            <p className="text-xs text-white/60 truncate">
                              {session?.email}
                            </p>
                          </div>
                        </div>

                        {/* Status + CTA */}
                        <div className="mt-3">
                          <div className="flex mt-4 items-center text-md text-white/90">
                            <FontAwesomeIcon
                              icon={faCrown}
                              className="mr-2 text-pcdl-4"
                            />
                            {session?.subscription_name || 'Member'}
                          </div>
                        </div>
                      </div>

                      {/* List */}
                      <div className="py-2">
                        <ProfileRow to="/plans" icon={StarIcon} inMenu>
                          Subscriptions Plans
                        </ProfileRow>

                        <ProfileRow to="/affiliate" icon={PlayIcon} inMenu>
                          Affiliate Program
                        </ProfileRow>

                        <ProfileRow
                          to="/affiliate/everydaysolutions"
                          icon={PlayIcon}
                          inMenu
                        >
                          Affiliate Everyday Solutions
                        </ProfileRow>
                      </div>

                      <div className="py-2 border-y border-white/10">
                        <ProfileRow to="/watchlist" icon={BookmarkIcon} inMenu>
                          Watchlist
                        </ProfileRow>
                        <ProfileRow to="/library" icon={ListBulletIcon} inMenu>
                          Library
                        </ProfileRow>
                        <ProfileRow to="/history" icon={ClockIcon} inMenu>
                          History
                        </ProfileRow>
                      </div>

                      <div className="py-2">
                        <ProfileRow to="/settings" icon={Cog6ToothIcon} inMenu>
                          Settings
                        </ProfileRow>
                        <ProfileRow
                          to="/help"
                          icon={ChatBubbleLeftEllipsisIcon}
                          inMenu
                        >
                          Chat Support
                        </ProfileRow>
                        {/* <ProfileRow to="/redeem/coupon" icon={GiftIcon} inMenu>
                          Gift Card
                        </ProfileRow> */}
                      </div>

                      {/* Wallet + logout */}
                      <div className="py-2 border-t border-white/10">
                        <div className="mx-4 mb-2 flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2 text-sm text-white">
                          <WalletIcon className="h-5 w-5 text-white/80" />
                          <span className="font-medium">{wallet}</span>
                          <span className="ml-1">Espees</span>
                          <Link
                            to="/wallet"
                            className="ml-auto text-xs underline opacity-80 hover:opacity-100"
                          >
                            Wallet
                          </Link>
                        </div>
                        <ProfileRow
                          icon={ArrowRightOnRectangleIcon}
                          onClick={() => {
                            navigate('/');
                            Logout();
                          }}
                          inMenu
                        >
                          {t('Logout')}
                        </ProfileRow>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>

                {/* Mobile button that opens the PROFILE sheet */}
                <button
                  type="button"
                  onClick={() => setProfileOpen(true)}
                  className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full
                    bg-white/10 ring-1 ring-white/15
                    hover:bg-white/15 hover:ring-white/30
                    focus:outline-none focus:ring-2 focus:ring-white/30 overflow-hidden"
                  aria-label="Open profile"
                >
                  {session?.avatar_url ? (
                    <img
                      src={`https://nmt.loveworldapis.com/api/${session.avatar_url}`}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faUser}
                      className="h-4 w-4 text-white"
                    />
                  )}
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/auth?mode=signup"
                  className="hidden md:inline-flex items-center rounded-full bg-white text-neutral-900 font-bold text-sm px-5 py-2 shadow-[0_1px_0_rgba(255,255,255,0.2)_inset] hover:brightness-95"
                >
                  {t('Sign Up')}
                </Link>
                <Link
                  to="/auth"
                  className="inline-flex items-center rounded-full border border-white/40 text-white font-bold text-sm px-5 py-2 hover:bg-white/10"
                >
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  {t('Sign In')}
                </Link>
              </div>
            )}

            {/* Location pill (flag) — desktop only */}
            {locReady && countryCode && (
              <div className="hidden lg:inline-flex items-center rounded-full px-3 py-1.5 text-sm text-white/90">
                <span className={`flag-icon flag-icon-${countryCode} mr-2`} />
              </div>
            )}
          </div>
        </div>
      </div>

      <PromoBanner
        open={promoOpen && Boolean(exposeCampaign)}
        onClose={() => setPromoOpen(false)}
        title={exposeCampaign?.name || ''}
        message=""
        ctaLabel="Participate"
        to="/expose"
        storageKey={`${PROMO_KEY}:${exposeCampaign?.name || ''}`}
        ttl={PROMO_TTL}
      />

      {/* ================== MOBILE MAIN MENU SHEET (Hamburger) ================== */}
      {!onWelcome && (
        <Transition show={mainOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setMainOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-out duration-150"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed left-0 right-0 top-16 bottom-0 bg-black/70" />
            </Transition.Child>

            <div className="fixed inset-0">
              <Transition.Child
                as={Fragment}
                // 👇 slide IN from the LEFT
                enter="transition transform duration-200"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition transform duration-200"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                {/* 👇 panel is anchored to the LEFT under the 64px navbar */}
                <Dialog.Panel
                  className="fixed left-0 top-16 h-[calc(100vh-4rem)] w-full max-w-[420px]
                       bg-neutral-900 text-white border-r border-white/10
                       shadow-2xl overflow-y-auto"
                >
                  {/* Body */}
                  <nav className="pb-6">
                    <div className="py-2">
                      <MobileRow to="/">{t('Home')}</MobileRow>

                      <MobileRow to="/browse">{t('Alphabetical')}</MobileRow>
                      <MobileRow to="/series/hGPFmF-VlVI/your-loveworld-specials">
                        {t('Your Loveworld Specials')}
                      </MobileRow>
                      <MobileRow to="/search">{t('PCDL Search')}</MobileRow>
                      <MobileRow to="/everydaysolutions">
                        {t('Everyday Solutions')}
                      </MobileRow>

                      <MobileCollapsible label={t('Categories')}>
                        <MobileRow to="/categories">
                          {t('All Categories')}
                        </MobileRow>
                        <MobileRow to="/everydaysolutions">
                          {t('Everyday Solutions')}
                        </MobileRow>
                        <MobileRow to="/categories/XR7p8b5icds/languages">
                          {t('Languages')}
                        </MobileRow>
                      </MobileCollapsible>

                      <MobileCollapsible label={t('More')}>
                        <MobileRow
                          to="/search"
                          onAfter={() => setSearchOpen(true)}
                        >
                          {t('PCDL Search')}
                        </MobileRow>
                        <MobileRow
                          to="https://pastorchrisdigitallibrary.org/livetv/"
                          external
                        >
                          {t('PCDL TV')}
                        </MobileRow>
                        <MobileRow to="/everydaysolutions/dictionary">
                          {t('Dictionary')}
                        </MobileRow>
                        <MobileRow to="/wordfest">{t('WordFest 7')}</MobileRow>
                        <MobileRow to="/4mot">
                          {t('4 Months Transformation Challenge')}
                        </MobileRow>
                        <MobileRow to="/pcdl4kids">Kids</MobileRow>
                      </MobileCollapsible>
                      <MobileRow to="/help">Chat Support</MobileRow>
                    </div>
                  </nav>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      )}

      {/* ================== MOBILE PROFILE SHEET ================== */}
      <Transition show={profileOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60] lg:hidden"
          onClose={setProfileOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-out duration-150"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed left-0 right-0 top-16 bottom-0 bg-black/70" />
          </Transition.Child>

          <div className="fixed inset-0">
            <Transition.Child
              as={Fragment}
              enter="transition transform duration-200"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transition transform duration-200"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel
                className="fixed right-0 top-16 h-[calc(100vh-4rem)] w-full max-w-[420px]
                                        bg-neutral-900 text-white border-l border-white/10 shadow-2xl overflow-y-auto"
              >
                {/* Header */}
                <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-white/10 overflow-hidden ring-1 ring-white/15">
                      {session?.avatar_url ? (
                        <img
                          src={`https://nmt.loveworldapis.com/api/${session.avatar_url}`}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <UserCircleIcon className="h-full w-full text-white/40 p-1" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className="text-white font-semibold">
                        {session?.first_name || session?.name || 'Profile'}
                      </p>
                      <p className="text-xs text-white/60">{session?.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setProfileOpen(false)}
                    className="rounded-md p-2 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/20"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Status + CTA */}
                <div className="px-4 pt-3 pb-4 border-b border-white/10">
                  <div className="flex items-center text-sm text-white/90">
                    <FontAwesomeIcon
                      icon={faCrown}
                      className="mr-2 text-pcdl-4"
                    />
                    {session?.subscription_name || 'Member'}
                  </div>

                  {/* {session?.subscription_name?.includes?.('Gold') && (
                    <Link
                      to="/subscriptions"
                      onClick={() => setProfileOpen(false)}
                      className="mt-3 text-sm inline-flex w-full items-center justify-center rounded-sm
                               bg-pcdl-4 px-4 py-2.5 font-bold text-white hover:brightness-95"
                    >
                      Upgrade your account
                    </Link>
                  )} */}
                </div>

                {/* Items */}
                <div className="py-2">
                  {/* <ProfileRow to="/profile/switch" icon={ArrowsRightLeftIcon} onClick={() => setProfileOpen(false)}>Switch Profile</ProfileRow> */}
                  <div className="py-2">
                    <ProfileRow to="/plans" icon={StarIcon} onClick={() => setProfileOpen(false)}>
                      Subscriptions Plans
                    </ProfileRow>

                    <ProfileRow to="/affiliate" icon={PlayIcon} onClick={() => setProfileOpen(false)}>
                      Affiliate Program
                    </ProfileRow>

                    <ProfileRow to="/affiliate/everydaysolutions" icon={PlayIcon} onClick={() => setProfileOpen(false)}>
                      Affiliate Everyday Solutions
                    </ProfileRow>
                  </div>
                </div>

                <div className="py-2 border-y border-white/10">
                  <ProfileRow
                    to="/watchlist"
                    icon={BookmarkIcon}
                    onClick={() => setProfileOpen(false)}
                  >
                    Watchlist
                  </ProfileRow>
                  <ProfileRow
                    to="/library"
                    icon={ListBulletIcon}
                    onClick={() => setProfileOpen(false)}
                  >
                    Library
                  </ProfileRow>
                  <ProfileRow
                    to="/history"
                    icon={ClockIcon}
                    onClick={() => setProfileOpen(false)}
                  >
                    History
                  </ProfileRow>
                </div>

                {/* <div className="py-2">
                  <ProfileRow to="/notifications" icon={BellIcon} onClick={() => setProfileOpen(false)}>Notifications</ProfileRow>
                  <ProfileRow to="/redeem/coupon" icon={GiftIcon} onClick={() => setProfileOpen(false)}>Gift Card</ProfileRow>
                </div> */}
                <ProfileRow
                  to="/settings"
                  icon={Cog6ToothIcon}
                  onClick={() => setProfileOpen(false)}
                >
                  Settings
                </ProfileRow>
                <div className="py-2 border-t border-white/10">
                  <div className="mx-4 mb-2 flex items-center gap-3 rounded-lg bg-white/10 px-3 py-2 text-sm text-white">
                    <WalletIcon className="h-5 w-5 text-white/80" />
                    <span className="font-medium">{wallet}</span>
                    <span className="ml-1">Espees</span>
                    <Link
                      to="/wallet"
                      onClick={() => setProfileOpen(false)}
                      className="ml-auto text-xs underline opacity-80 hover:opacity-100"
                    >
                      Wallet
                    </Link>
                  </div>
                  <ProfileRow
                    icon={ArrowRightOnRectangleIcon}
                    onClick={() => {
                      setProfileOpen(false);
                      navigate('/');
                      Logout();
                    }}
                  >
                    {t('Logout')}
                  </ProfileRow>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Top search row */}
      {searchOpen && (
        <div className="border-t border-white/10 bg-neutral-900/80">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 py-3">
            <SearchBar placeholder="Search" btn={setSearchOpen} />
          </div>
        </div>
      )}
    </header>
  );
}
